import { featureCollection, point, Units } from '@turf/helpers';
import buffer from '@turf/buffer';
import bbox from '@turf/bbox';
import squareGrid from '@turf/square-grid';
import distance from '@turf/distance';
import centerOfMass from '@turf/center-of-mass';
import { Coordinate } from '../../models/coordinate';

/*
  A bounding box is composed of:
  ---
  [ min(x), min(y), max(x), max(y) ] or
  [ sw_lng, sw_lat, ne_lng, ne_lat ] or
  [ west,   south,  east,   north  ] or
  [ left,   bottom, right,  top    ]
 */
export type BBox = [number, number, number, number] | [number, number, number, number, number, number]

/**
 * Converts a turf bbox into google maps bounds
 * @param bbox
 * @return {google.maps.LatLngBounds}
 */
const bboxToBounds = (bbox: BBox): google.maps.LatLngBounds => {
  const [sw_lng, sw_lat, ne_lng, ne_lat] = bbox;

  return new google.maps.LatLngBounds(
      new google.maps.LatLng(sw_lat, sw_lng),
      new google.maps.LatLng(ne_lat, ne_lng),
  );
};

/**
 * Given a set of google map positions, return the buffered bounds around
 * @param positions
 * @param bufferInMeters
 */
export const getFittedBoundsAroundPoints = (
    {
      positions,
      // Default to 42m in each cardinal direction.
      // We're going this low because the google streetview pegman
      // has a relatively large intrinsic width and height of ~50px
      // which renders marker clicks unusable unless the user zooms in
      // -- which we're essentially doing with this buffer.
      bufferInMeters = 42,
    }: {
      positions: google.maps.LatLngLiteral[],
      bufferInMeters?: number
    }): ReturnType<typeof bboxToBounds> => {

  if (!positions?.length) {
    console.warn('Fitting an empty collection of points is a daring task. Check your input.', {
      positions,
      bufferInMeters,
    });
    return new google.maps.LatLngBounds();
  }

  const collection = featureCollection(
      positions.map(pos => point(
          // in geojson, lng precedes lat!
          [pos.lng, pos.lat],
      )),
  );

  const bufferAroundCollection = buffer(collection, bufferInMeters, { units: 'meters' });
  const bboxOfBuffer = bbox(bufferAroundCollection);

  return bboxToBounds(bboxOfBuffer);
};

/**
 * Converts a set of google maps bounds into a turf-able bbox
 * @param {google.maps.LatLngBounds} bounds
 * @return {BBox}
 */
const boundsToBbox = (bounds: google.maps.LatLngBounds): BBox => {
  const { south, east, north, west } = bounds.toJSON();
  return [west, south, east, north];
};

/**
 * Takes a set of gmaps bounds, calculates its bbox, fits 9 square polygons into these bounds,
 * and returns their centers.
 * @param bounds
 * @returns The centers of the squares filling up the bbox
 */
export const splitBoundsIntoSquareCenters = (bounds: google.maps.LatLngBounds): {
  centers: google.maps.LatLngLiteral[],
  searchRadius: number
} => {
  const bbox = boundsToBbox(bounds);
  const [west, south, east, north] = bbox;

  // let's figure out the map dimensions based on the bbox
  // NW  _____________ NE
  //     |   #some   |
  //     |   #map    |
  // SW  |___________| SE

  const mapWidth = distance(
      point([west, south]),
      point([east, south]),
      { units: 'meters' },
  );

  const mapHeight = distance(
      point([west, south]),
      point([west, north]),
      { units: 'meters' },
  );

  // we want squares to fill the map and if we take the *largest* dimension,
  // some squares won't fit into the viewport and will be undefined!
  const smallestMapDimension = Math.min.apply(null, [mapWidth, mapHeight]);

  // 3x horiz x 3x vert = 9
  const cellSide = (
      // downsize the whole bbox to 90% to ensure that *all* squares can be fitted
      // to the bbox viewport
      (0.9 * smallestMapDimension)
  ) / 3;

  const grid = squareGrid(bbox, cellSide, { units: 'meters' });
  const mapCenter = point([bounds.getCenter().lng(), bounds.getCenter().lat()]);
  const centers = grid.features
      // iterate and calculate centers
      .map(squarePoly => centerOfMass(squarePoly))
      // sort by being closest to the map center (smallest distance -> largest)
      .sort((centerA, centerB) => {
        const dist_a = distance(centerA, mapCenter, { units: 'meters' });
        const dist_b = distance(centerB, mapCenter, { units: 'meters' });
        return dist_a < dist_b ? -1 : 1;
      })
      // convert to gmaps lat lng literals
      .map(point => {
        const [lng, lat] = point.geometry.coordinates;
        return { lat, lng };
      });


  return {
    // the centers in CCW direction, starting w/ the center-most one
    centers,
    searchRadius: cellSide / 2, // radius is 1/2 of the square width
  };
};

/**
 * Uses turf's distance helper when `google.maps.geometry` is not (yet) available.
 * @param p1
 * @param p2
 * @param units
 */
export const calculateGeoPointDistance = (
    p1: Coordinate,
    p2: Coordinate,
    units: Units = 'meters',
) => {
  const turfPoint1 = point([p1.longitude, p1.latitude]);
  const turfPoint2 = point([p2.longitude, p2.latitude]);

  return distance(turfPoint1, turfPoint2, { units });
};

/**
 * Convert strings of the form "lat, lon" to a `Coordinate` object.
 * @param str
 */
export const coordinateStringToObj = (str: string): Coordinate => {
  // https://regex101.com/r/eCV8FX/1
  const [lat, lon] = str.split(/,\s?\n?/);
  return {
    latitude: parseFloat(lat),
    longitude: parseFloat(lon),
  };
};
